import { OTP_STATE, type OtpState } from '@/utils';

interface OtpContent {
  heading: string;
  description: string;
}

const DEFAULT_CONTENT: OtpContent = {
  heading: "Confirm it's you",
  description: 'Enter the code we sent to your email address',
};

const NEW_CODE_REQUIRED = 'You need to request a new code to continue';
const SEND_LIMITED_HEADING = 'Too many code requests';

const CONTENT_BY_STATE: Partial<Record<OtpState, OtpContent>> = {
  [OTP_STATE.BLOCKED]: {
    heading: 'Account temporarily locked',
    description: 'Too many incorrect attempts to log in.',
  },
  [OTP_STATE.SEND_LIMITED]: {
    heading: SEND_LIMITED_HEADING,
    description: 'Check your spam folder in case it ended up there',
  },
  [OTP_STATE.ATTEMPTS_EXHAUSTED]: {
    heading: 'Too many login attempts',
    description: NEW_CODE_REQUIRED,
  },
};

export function usesRetryTimer(state: OtpState): boolean {
  return state === OTP_STATE.SEND_LIMITED;
}

function getContent(state: OtpState, isWaiting: boolean): OtpContent {
  const isSendLimitExpired = state === OTP_STATE.SEND_LIMITED && !isWaiting;

  if (isSendLimitExpired) {
    return { heading: SEND_LIMITED_HEADING, description: NEW_CODE_REQUIRED };
  }

  return CONTENT_BY_STATE[state] ?? DEFAULT_CONTENT;
}

/** Inline error under the code input, if the state has one. */
export function getOtpErrorMessage(state: OtpState): string | undefined {
  switch (state) {
    case OTP_STATE.INCORRECT:
      return "That code didn't work, check email and try again.";
    case OTP_STATE.ERROR:
      return 'We could not verify your code. Please try again.';
    default:
      return undefined;
  }
}

/** What the OTP screen shows for a state; `isWaiting` is whether its timer is still running. */
export function deriveOtpScreen(state: OtpState, isWaiting: boolean) {
  const isLockedOut = state === OTP_STATE.BLOCKED;
  const isExhausted = state === OTP_STATE.ATTEMPTS_EXHAUSTED;
  const isSendLimited = state === OTP_STATE.SEND_LIMITED;
  const isVerifying = state === OTP_STATE.VERIFYING;

  return {
    ...getContent(state, isWaiting),
    isLockedOut,
    isVerifying,
    showLockIcon: isLockedOut || isExhausted || (isSendLimited && isWaiting),
    showCodeInput: !isLockedOut && !isExhausted,
    isCodeInputDisabled: isSendLimited,
    showResendTimer: !isLockedOut && isWaiting,
    canResend: !isLockedOut && !isVerifying && !isWaiting,
  };
}
